"use client";

import type { FeaturedBrokerData } from "@/app/(saas)/(app)/business-brokers/_lib/featured-brokers";
import { BrokerProfileCard } from "@/components/brokers/broker-profile-card";
import { useEffect, useState } from "react";

export type FeaturedBrokersProps = {
  brokers: FeaturedBrokerData[];
  locationLabel?: string;
};

export function FeaturedBrokers({
  brokers,
  locationLabel,
}: FeaturedBrokersProps) {
  const [showSwipeHint, setShowSwipeHint] = useState(true);

  useEffect(() => {
    try {
      const hasInteracted =
        window.localStorage.getItem("featured-brokers-swipe-hint-dismissed") ===
        "1";
      if (hasInteracted) setShowSwipeHint(false);
    } catch {
      // localStorage may be unavailable in strict privacy modes
    }
  }, []);

  function dismissSwipeHint() {
    if (!showSwipeHint) return;
    setShowSwipeHint(false);
    try {
      window.localStorage.setItem("featured-brokers-swipe-hint-dismissed", "1");
    } catch {
      // no-op when localStorage is unavailable
    }
  }

  if (brokers.length === 0) return null;

  function renderCard(broker: FeaturedBrokerData) {
    const name = broker.name ?? "Verified broker";
    const firstName = broker.name?.split(" ")[0] ?? "broker";

    return (
      <BrokerProfileCard
        href={`/u/${broker.agentSlug}`}
        id={broker.id}
        name={name}
        image={broker.image}
        isVerified={broker.isVerified}
        organization={
          broker.organizationName ?? "Independent business broker"
        }
        specialties={broker.specialties}
        listingsCount={broker.listingCount}
        eyebrow="Featured"
        ctaLabel={`Contact ${firstName}`}
      />
    );
  }

  return (
    <section className="relative mb-8 overflow-hidden rounded-[24px]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,#FFFFFF_0%,#F4F9F7_38%,theme-blue-soft_72%,#FFFFFF_100%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.16] [background-size:30px_30px] [background-image:linear-gradient(to_right,rgba(10,10,10,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(10,10,10,0.08)_1px,transparent_1px)]" />
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(0,200,83,0.12),rgba(255,255,255,0)_72%)] blur-xl" />
      <div className="pointer-events-none absolute -right-28 bottom-0 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(41,98,255,0.12),rgba(255,255,255,0)_74%)] blur-xl" />
      <div className="relative rounded-[24px] border border-foreground/10 bg-card/88 px-4 py-5 shadow-[0_14px_34px_rgba(0,0,0,0.06)] backdrop-blur-[2px] md:px-5">
        <p className="text-[11px] font-semibold text-[var(--venturu-gray-400)] uppercase tracking-[0.12em] mb-1">
          Top picks
        </p>
        <h2 className="text-base md:text-sm font-semibold text-[var(--venturu-gray-500)] md:uppercase md:tracking-wide mb-4">
          {locationLabel
            ? `Featured brokers in ${locationLabel}`
            : "Featured brokers"}
        </h2>
        {showSwipeHint && (
          <p className="text-xs text-[var(--venturu-gray-400)] mb-3 md:hidden">
            Swipe to see more brokers →
          </p>
        )}

        <div
          className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory md:hidden"
          onTouchStart={dismissSwipeHint}
          onPointerDown={dismissSwipeHint}
        >
          {brokers.map((broker) => (
            <div key={broker.id} className="h-full min-w-[86%] snap-start">
              {renderCard(broker)}
            </div>
          ))}
        </div>

        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brokers.map((broker) => (
            <div key={broker.id} className="h-full">
              {renderCard(broker)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
